/**
 * 获取当前页面 origin
 */
export function getOrigin(): string {
    const {protocol, hostname, port} = window.location;
    if (window.location.origin) {
        return window.location.origin;
    }
    return protocol + '//' + hostname + (port ? ':' + port : '');
}

/**
 * 防抖函数
 */
export interface DebouncedFunction<Args extends unknown[]> {
    (...args: Args): void;
    cancel: () => void;
}

export const debounce = <Args extends unknown[], R>(
    fn: (...args: Args) => R,
    delay = 300,
): DebouncedFunction<Args> => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    const debounced = (...args: Args) => {
        clearTimeout(timer ?? undefined);
        timer = setTimeout(() => fn(...args), delay);
    };

    debounced.cancel = () => {
        clearTimeout(timer ?? undefined);
        timer = null;
    };

    return debounced;
};

/**
 * 节流函数
 */
export const throttle = <T extends (...args: unknown[]) => unknown>(
    fn: T,
    delay = 300,
): ((...args: Parameters<T>) => void) => {
    let lastTime = 0;
    return (...args: Parameters<T>) => {
        const now = Date.now();
        if (now - lastTime >= delay) {
            lastTime = now;
            fn(...args);
        }
    };
};

/**
 * 深拷贝
 */
export const deepClone = <T>(obj: T): T => {
    return structuredClone(obj);
};

/**
 * 写入多种 MIME 格式的剪贴板内容（如 text/plain + application/x-lexical-editor）。
 *
 * 走 execCommand('copy') 而非异步 Clipboard API：后者不支持自定义 MIME，无法保留
 * Lexical 结构（mention 节点）。捕获阶段自行 setData 并阻断事件继续冒泡，
 * 避免画布 tldraw 的全局 copy 监听覆盖内容。非安全上下文（HTTP）同样可用。
 */
export const writeClipboardData = (data: Record<string, string>): boolean => {
    const handleCopy = (event: ClipboardEvent) => {
        event.stopImmediatePropagation();
        event.preventDefault();
        const clipboardData = event.clipboardData;
        if (!clipboardData) {
            return;
        }
        Object.entries(data).forEach(([type, value]) => {
            clipboardData.setData(type, value);
        });
    };

    const textarea = document.createElement('textarea');
    textarea.value = data['text/plain'] ?? ' ';
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-1000px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    document.addEventListener('copy', handleCopy, true);

    let ok = false;
    try {
        textarea.focus();
        textarea.select();
        ok = document.execCommand('copy');
    } catch {
        // execCommand 抛出时保持 ok = false
    } finally {
        document.removeEventListener('copy', handleCopy, true);
        textarea.remove();
    }

    return ok;
};

/**
 * 将文本写入剪贴板
 * 优先走 navigator.clipboard，非安全上下文（HTTP）或权限受限时回退到 execCommand。
 * @returns { ok: boolean; msg: string } ok 表示是否成功，msg 为对应提示文案
 */
export const copyTextToClipboard = async (text: string): Promise<{ok: boolean; msg: string}> => {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return {ok: true, msg: '已复制到剪贴板'};
        }
    } catch {
        // 忽略，转入 execCommand 兜底
    }

    return writeClipboardData({'text/plain': text})
        ? {ok: true, msg: '已复制到剪贴板'}
        : {ok: false, msg: '复制失败，请手动复制'};
};
export const generateId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

/**
 * 生成符合 RFC4122 标准的 UUID v4
 * 优先使用原生 crypto.randomUUID()，不支持则降级为手动实现
 */
export const generateUuid = (): string => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    // 降级实现：生成格式为 xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx 的 UUID v4
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

/**
 * 本地存储工具
 */
export const storage = {
    get<T>(key: string): T | null {
        try {
            const value = localStorage.getItem(key);
            return value ? (JSON.parse(value) as T) : null;
        } catch {
            return null;
        }
    },
    set(key: string, value: unknown): void {
        localStorage.setItem(key, JSON.stringify(value));
    },
    remove(key: string): void {
        localStorage.removeItem(key);
    },
    clear(): void {
        localStorage.clear();
    },
};
